import styles from './SplitScreen.module.css';

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

/**
 * Компонент SplitScreen используется для создания разделенного интерфейса из двух панелей
 * с возможностью изменения их размеров. Это удобный инструмент для построения адаптивных
 * интерфейсов, таких как текстовые редакторы, панели управления или разделы приложения.
 *
 * @component
 *
 * @param {Object} props Свойства компонента.
 * @param {('vertical'|'horizontal')} [props.orientation='vertical'] Ориентация разделения панелей:
 *  - `'vertical'` — панели расположены по вертикали (левая и правая).
 *  - `'horizontal'` — панели расположены по горизонтали (верхняя и нижняя).
 * @param {boolean} [props.allowResize=true] Разрешить изменение размеров панелей с помощью разделителя.
 * @param {number} [props.minSize=100] Минимальный размер одной панели (в пикселях), чтобы предотвратить их полное скрытие.
 * @param {number} [props.initialFirstPaneSize=50] Процентная ширина/высота первой панели в начальном состоянии.
 * @param {number} [props.initialSecondPaneSize=50] Процентная ширина/высота второй панели в начальном состоянии.
 * @param {string} props.styleDivider Кастомные стили для разделителя блоков.
 * @param {React.ReactNode[]} props.children Два дочерних элемента, которые будут расположены в панелях.
 *
 * @example
 * // Пример использования с вертикальной ориентацией
 * <SplitScreen
 *   orientation="vertical"
 *   allowResize={true}
 *   minSize={100}
 *   initialFirstPaneSize={40}
 *   initialSecondPaneSize={60}
 * >
 *   <div>Левая панель</div>
 *   <div>Правая панель</div>
 * </SplitScreen>
 *
 * @example
 * // Пример использования с горизонтальной ориентацией
 * <SplitScreen
 *   orientation="horizontal"
 *   allowResize={true}
 *   minSize={150}
 *   initialFirstPaneSize={50}
 *   initialSecondPaneSize={50}
 * >
 *   <div>Верхняя панель</div>
 *   <div>Нижняя панель</div>
 * </SplitScreen>
 */
const SplitScreen = ({
  orientation = 'vertical',
  allowResize = true,
  minSizeFirstPane = 10, // в процентах
  maxSizeFirstPane = 90, // в процентах
  initialFirstPaneSize = 50,
  initialSecondPaneSize = 50,
  children,
  styleDivider,
}) => {
  // Проверяем, что передано ровно 2 дочерних элемента
  if (React.Children.count(children) !== 2) {
    throw new Error('SplitScreen компонент требует ровно 2 дочерних элемента.');
  }

  // Текущее положение разделителя в процентах. По умолчанию — размер первой панели.
  const [dividerPos, setDividerPos] = useState(initialFirstPaneSize);
  const [isDragging, setIsDragging] = useState(false);

  // Ссылка на контейнер для вычисления размеров
  const containerRef = useRef(null);

  // Проверяем, чтобы начальные размеры панелей в сумме составляли 100%
  useEffect(() => {
    if (initialFirstPaneSize + initialSecondPaneSize !== 100) {
      console.warn(
        'Суммарный размер initialFirstPaneSize и initialSecondPaneSize должен быть равен 100. Значения будут скорректированы.',
      );
      // Корректируем положение разделителя, если задано неверно
      setDividerPos(Math.min(initialFirstPaneSize, 100));
    }
  }, [initialFirstPaneSize, initialSecondPaneSize]);

  // Обработка движения мыши для изменения размеров панелей
  const handleMouseMove = (e) => {
    if (!allowResize) return;

    const container = containerRef.current;
    if (!container) return;

    if (orientation === 'horizontal') {
      const containerHeight = container.offsetHeight;
      const offsetTop = container.getBoundingClientRect().top;

      const newHeight = ((e.clientY - offsetTop) / containerHeight) * 100;

      const clampedHeight = Math.max(minSizeFirstPane, Math.min(maxSizeFirstPane, newHeight));

      setDividerPos(clampedHeight);
    } else {
      const containerWidth = container.offsetWidth;
      const offsetLeft = container.getBoundingClientRect().left;

      const newWidth = ((e.clientX - offsetLeft) / containerWidth) * 100;

      const clampedWidth = Math.max(minSizeFirstPane, Math.min(maxSizeFirstPane, newWidth));

      setDividerPos(clampedWidth);
    }
  };

  // Удаляем обработчики событий мыши при завершении изменения размеров
  const handleMouseUp = () => {
    setIsDragging(false);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  // Добавляем обработчики событий мыши при начале изменения размеров
  const handleMouseDown = () => {
    if (allowResize) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      setIsDragging(true);
    }
  };

  const styleFirstContainer =
    orientation === 'horizontal' ? { height: `${dividerPos}%` } : { width: `${dividerPos}%` };

  const styleSecondContainer =
    orientation === 'horizontal'
      ? { height: `${100 - dividerPos}%` }
      : { width: `${100 - dividerPos}%` };

  return (
    <div
      className={classNames(styles.splitScreen, {
        [styles.horizontal]: orientation === 'horizontal',
        [styles.vertical]: orientation === 'vertical',
        [styles.noSelect]: isDragging,
      })}
      ref={containerRef}
    >
      {/* Первая панель */}
      <div className={styles.pane} style={styleFirstContainer}>
        {children[0]}
      </div>
      {/* Разделитель */}
      <div
        className={classNames(styles.divider, styleDivider, {
          [styles.isDragging]: isDragging,
          [styles.dividerHorizontal]: orientation === 'horizontal',
          [styles.dividerVertical]: orientation === 'vertical',
        })}
        onMouseDown={handleMouseDown}
        style={
          allowResize
            ? {
                cursor: orientation === 'horizontal' ? 'ns-resize' : 'ew-resize',
              }
            : {
                opacity: 0,
                cursor: 'inherit',
              }
        }
      ></div>
      {/* Вторая панель */}
      <div className={styles.pane} style={styleSecondContainer}>
        {children[1]}
      </div>
    </div>
  );
};

SplitScreen.propTypes = {
  orientation: PropTypes.oneOf(['vertical', 'horizontal']),
  allowResize: PropTypes.bool,
  minSizeFirstPane: PropTypes.number, // в процентах
  maxSizeFirstPane: PropTypes.number, // в процентах
  initialFirstPaneSize: PropTypes.number,
  initialSecondPaneSize: PropTypes.number,
  styleDivider: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default SplitScreen;
